import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminShell from "../AdminShell";
import ConfirmModal from "../ConfirmModal";
import { adminFetch } from "../adminApi";
import { useAdminToast } from "../AdminToast";
import "./admin-bookings.css";

/**
 * RAdminBookings — redesigned /admin/bookings (Tour Bookings).
 *
 * Functional ground truth: frontend/src/pages/AdminBookingsPage.js
 * Visual/interaction ground truth: design-ref/admin/Bookings.html (+ .logic.js)
 *
 * Legacy behaviours carried over:
 *  - mount: GET /api/admin/bookings + GET /api/admin/bookings/stats
 *  - date filter: GET /api/admin/bookings/date/{date}; "Show All" clears it
 *  - check-in toggle: PUT /api/admin/bookings/{id}
 *      { checked_in: !current, status: current ? "confirmed" : "checked_in" }
 *      per-booking loading, then refresh list (respecting filter) + stats
 *  - edit tour: PUT /api/admin/bookings/{id} { tour_date, tour_time }
 *      (design's modal replaces the legacy inline edit; same call, list
 *       refresh only — the legacy page did NOT refetch stats after an edit)
 *  - ticket: GET /api/admin/bookings/{id}/ticket → blob download
 *      UmoonaTourTicket-{booking_reference}.pdf
 *  - delete: DELETE /api/admin/bookings/{id} (design ConfirmModal replaces
 *      window.confirm), then refresh list + stats
 *  - status badges: checked_in → Checked In, confirmed → Confirmed, else Pending
 *  - canonical times 10:00 / 14:00 / 16:00 → 10:00 AM / 2:00 PM / 4:00 PM
 *  - id fallback booking.id || booking._id everywhere
 */

const TIME_OPTIONS = [
  ["10:00", "10:00 AM"],
  ["14:00", "2:00 PM"],
  ["16:00", "4:00 PM"],
];

const TIME_LABELS = {
  "10:00": "10:00 AM",
  "14:00": "2:00 PM",
  "16:00": "4:00 PM",
};

const TICKET_TYPE_LABELS = {
  adult: "Adult",
  child: "Child",
  family: "Family",
};

const audFormatter = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
});

const bookingId = (booking) => booking.id || booking._id;

const formatTime = (timeString) => TIME_LABELS[timeString] || timeString;

/* Card-row date — design shows "1 Jul 2026" */
const formatDateShort = (dateString) =>
  new Date(dateString).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

/* Section-heading date — legacy long format ("Wednesday, 1 July 2026") */
const formatDateLong = (dateString) =>
  new Date(dateString).toLocaleDateString("en-AU", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const getTicketSummary = (tickets) =>
  (tickets || [])
    .map(
      (ticket) =>
        `${ticket.quantity}× ${
          TICKET_TYPE_LABELS[ticket.ticket_type] || ticket.ticket_type
        }`
    )
    .join(", ");

const getStatusChip = (booking) => {
  if (booking.checked_in) return ["adm-chip--teal", "Checked In"];
  if (booking.status === "confirmed") return ["adm-chip--blue", "Confirmed"];
  return ["adm-chip--amber", "Pending"];
};

const RAdminBookings = () => {
  const navigate = useNavigate();
  const toast = useAdminToast();

  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedDate, setSelectedDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [checkinLoading, setCheckinLoading] = useState({});

  // Edit Tour modal
  const [editingBooking, setEditingBooking] = useState(null); // booking object
  const [editForm, setEditForm] = useState({ tour_date: "", tour_time: "" });
  const [updateLoading, setUpdateLoading] = useState(false);

  // Delete confirm
  const [deletingBooking, setDeletingBooking] = useState(null); // booking object
  const [deleteBusy, setDeleteBusy] = useState(false);

  /* ---- data loading (legacy fetchBookings / fetchBookingsByDate) ---- */
  const loadBookings = useCallback(
    async (date) => {
      try {
        setLoading(true);
        const res = await adminFetch(
          date ? `/api/admin/bookings/date/${date}` : "/api/admin/bookings",
          { navigate }
        );
        if (res.ok) {
          const data = await res.json();
          setBookings(data.bookings || []);
        } else {
          toast.error(
            date
              ? "Failed to fetch bookings for selected date"
              : "Failed to fetch bookings"
          );
        }
      } catch (error) {
        if (!error.isAuthError) {
          console.error("Error fetching bookings:", error);
          toast.error(
            date ? "Error fetching bookings by date" : "Error fetching bookings"
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [navigate, toast]
  );

  const fetchStats = useCallback(async () => {
    try {
      const res = await adminFetch("/api/admin/bookings/stats", { navigate });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      // legacy page logged silently — no user-facing error for stats
      if (!error.isAuthError) {
        console.error("Error fetching booking stats:", error);
      }
    }
  }, [navigate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Legacy: selectedDate set → fetch by date, cleared → fetch all
  useEffect(() => {
    loadBookings(selectedDate);
  }, [selectedDate, loadBookings]);

  const refreshAll = () => {
    loadBookings(selectedDate);
    fetchStats();
  };

  /* ---- check-in toggle (same PUT the legacy checkbox used) ---- */
  const handleCheckIn = async (booking) => {
    const id = bookingId(booking);
    const currentStatus = booking.checked_in;
    try {
      setCheckinLoading((prev) => ({ ...prev, [id]: true }));
      const res = await adminFetch(`/api/admin/bookings/${id}`, {
        method: "PUT",
        navigate,
        body: JSON.stringify({
          checked_in: !currentStatus,
          status: currentStatus ? "confirmed" : "checked_in",
        }),
      });
      if (res.ok) {
        toast.success(
          !currentStatus ? "✓ Guest checked in" : "Check-in reverted"
        );
        loadBookings(selectedDate);
        fetchStats();
      } else {
        toast.error("Failed to update check-in status");
      }
    } catch (error) {
      if (!error.isAuthError) {
        console.error("Error updating check-in:", error);
        toast.error("Error updating check-in");
      }
    } finally {
      setCheckinLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  /* ---- ticket download (same endpoint/blob flow as legacy) ---- */
  const downloadTicket = async (booking) => {
    const id = bookingId(booking);
    const bookingReference = booking.booking_reference;
    try {
      toast.info(`⤓ Downloading UmoonaTourTicket-${bookingReference}.pdf`);
      const res = await adminFetch(`/api/admin/bookings/${id}/ticket`, {
        navigate,
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `UmoonaTourTicket-${bookingReference}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        toast.error("Failed to download ticket");
      }
    } catch (error) {
      if (!error.isAuthError) {
        console.error("Error downloading ticket:", error);
        toast.error("Error downloading ticket");
      }
    }
  };

  /* ---- delete (design confirm modal → same DELETE as legacy) ---- */
  const confirmDelete = async () => {
    if (!deletingBooking) return;
    const id = bookingId(deletingBooking);
    try {
      setDeleteBusy(true);
      const res = await adminFetch(`/api/admin/bookings/${id}`, {
        method: "DELETE",
        navigate,
      });
      if (res.ok) {
        loadBookings(selectedDate);
        fetchStats();
        toast.success("Booking deleted");
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to delete booking");
      }
    } catch (error) {
      if (!error.isAuthError) {
        console.error("Error deleting booking:", error);
        toast.error("Error deleting booking");
      }
    } finally {
      setDeleteBusy(false);
      setDeletingBooking(null);
    }
  };

  /* ---- Edit Tour modal (design modal → same PUT as legacy inline edit) ---- */
  const startEditing = (booking) => {
    setEditingBooking(booking);
    setEditForm({
      tour_date: booking.tour_date,
      tour_time: booking.tour_time,
    });
  };

  const cancelEditing = useCallback(() => {
    setEditingBooking(null);
    setEditForm({ tour_date: "", tour_time: "" });
  }, []);

  const handleUpdateTour = async () => {
    if (!editingBooking) return;
    const id = bookingId(editingBooking);
    try {
      setUpdateLoading(true);
      const res = await adminFetch(`/api/admin/bookings/${id}`, {
        method: "PUT",
        navigate,
        body: JSON.stringify({
          tour_date: editForm.tour_date,
          tour_time: editForm.tour_time,
        }),
      });
      if (res.ok) {
        // Legacy: refresh list (respecting filter) but NOT stats after an edit
        loadBookings(selectedDate);
        setEditingBooking(null);
        setEditForm({ tour_date: "", tour_time: "" });
        toast.success("✓ Booking updated");
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to update booking");
      }
    } catch (error) {
      if (!error.isAuthError) {
        console.error("Error updating booking:", error);
        toast.error("Error updating booking");
      }
    } finally {
      setUpdateLoading(false);
    }
  };

  // Escape closes the Edit Tour modal (mirrors ConfirmModal behaviour)
  useEffect(() => {
    if (!editingBooking) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !updateLoading) cancelEditing();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [editingBooking, updateLoading, cancelEditing]);

  const editTimeIsCustom =
    editForm.tour_time &&
    !TIME_OPTIONS.some(([value]) => value === editForm.tour_time);

  return (
    <AdminShell
      active="bookings"
      pageClass="pg-admin-bookings"
      eyebrow="Tours"
      title="Tour Bookings"
      actions={
        /* AdminShell mounts `uma` + pageClass on the same root element, so
           page-scoped `.uma .pg-admin-bookings …` selectors need this inner
           scope span (same precedent as RAdminLogin's inner wrapper). */
        <span className="pg-admin-bookings bk-hdractions">
          <input
            type="date"
            className="bk-date bk-date--hdr adm-hidesm"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            aria-label="Filter by Date"
            title="Filter by Date"
          />
          <button
            type="button"
            className="adm-iconbtn"
            onClick={refreshAll}
            title="Refresh"
            aria-label="Refresh"
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="#2a2018"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 11a8 8 0 10-2 6" />
              <path d="M20 4v5h-5" />
            </svg>
          </button>
        </span>
      }
    >
      {/* inner scope element so `.uma .pg-admin-bookings …` selectors match */}
      <div className="pg-admin-bookings">
      {/* ---- KPI row ---- */}
      <div className="adm-grid4 bk-kpis">
        <div className="adm-stat">
          <div className="adm-stat-top">
            <span className="adm-stat-label">Total Bookings</span>
            <span className="adm-stat-icon adm-stat-icon--teal" />
          </div>
          <div className="adm-stat-value">
            {(stats.total_bookings || 0).toLocaleString("en-AU")}
          </div>
          <div className="adm-stat-sub adm-stat-sub--teal">All time</div>
        </div>
        <div className="adm-stat">
          <div className="adm-stat-top">
            <span className="adm-stat-label">Confirmed</span>
            <span className="adm-stat-icon adm-stat-icon--blue" />
          </div>
          <div className="adm-stat-value">
            {(stats.confirmed_bookings || 0).toLocaleString("en-AU")}
          </div>
          <div className="adm-stat-sub adm-stat-sub--blue">
            Upcoming &amp; completed
          </div>
        </div>
        <div className="adm-stat">
          <div className="adm-stat-top">
            <span className="adm-stat-label">Today's Tours</span>
            <span className="adm-stat-icon adm-stat-icon--ember" />
          </div>
          <div className="adm-stat-value">
            {(stats.todays_bookings || 0).toLocaleString("en-AU")}
          </div>
          <div className="adm-stat-sub adm-stat-sub--ember">
            Scheduled today
          </div>
        </div>
        <div className="adm-stat">
          <div className="adm-stat-top">
            <span className="adm-stat-label">Total Revenue</span>
            <span className="adm-stat-icon adm-stat-icon--purple" />
          </div>
          <div className="adm-stat-value">
            {audFormatter.format(stats.total_revenue || 0)}
          </div>
          <div className="adm-stat-sub adm-stat-sub--purple">From bookings</div>
        </div>
      </div>

      {/* ---- section head: title + date filter ---- */}
      <div className="bk-secrow">
        <div className="bk-sectitle">
          {selectedDate
            ? `Bookings for ${formatDateLong(selectedDate)}`
            : "All bookings"}
          <span className="bk-count">
            {bookings.length} booking{bookings.length !== 1 ? "s" : ""} found
          </span>
        </div>
        <div className="bk-filters">
          <input
            type="date"
            className="bk-date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            aria-label="Filter by Date"
            title="Filter by Date"
          />
          <button
            type="button"
            className="adm-btn adm-btn--ghost bk-showall"
            onClick={() => setSelectedDate("")}
          >
            Show All
          </button>
        </div>
      </div>

      {/* ---- booking card-rows ---- */}
      {loading ? (
        <div className="adm-loading">
          <div className="adm-spinner" />
          Loading bookings…
        </div>
      ) : bookings.length === 0 ? (
        <div className="adm-empty">
          <div className="adm-empty-icon">🎫</div>
          <div className="adm-empty-title">No bookings found</div>
          <div className="adm-empty-sub">
            No bookings found for the selected criteria.
          </div>
        </div>
      ) : (
        <div className="bk-list">
          {bookings.map((booking) => {
            const id = bookingId(booking);
            const [chipClass, chipLabel] = getStatusChip(booking);
            return (
              <div key={id} className="bk-card">
                <div className="bk-row">
                  <div className="bk-info">
                    <div className="bk-refline">
                      <span className="bk-ref">
                        {booking.booking_reference}
                      </span>
                      <span className={`adm-chip ${chipClass}`}>
                        {chipLabel}
                      </span>
                    </div>
                    <div className="bk-cust">
                      {booking.customer?.name} • {booking.customer?.email}
                    </div>
                    <div className="bk-detail">
                      <div>
                        <div className="bk-dlabel">Tour Date</div>
                        <div className="bk-dval">
                          {formatDateShort(booking.tour_date)}
                        </div>
                      </div>
                      <div>
                        <div className="bk-dlabel">Tour Time</div>
                        <div className="bk-dval">
                          {formatTime(booking.tour_time)}
                        </div>
                      </div>
                      <div>
                        <div className="bk-dlabel">Tickets</div>
                        <div className="bk-dval">
                          {getTicketSummary(booking.tickets)}
                        </div>
                      </div>
                      <div>
                        <div className="bk-dlabel">Total</div>
                        <div className="bk-dval">
                          {audFormatter.format(booking.total_amount || 0)}
                          {booking.coupon_code && (
                            <>
                              {" "}
                              <span
                                className="adm-chip adm-chip--teal"
                                title={`${booking.discount_percent}% coupon discount`}
                              >
                                {booking.coupon_code}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {booking.customer_notes && (
                      <div className="bk-note">
                        📝 {booking.customer_notes}
                      </div>
                    )}
                  </div>
                  <div className="bk-actions">
                    <button
                      type="button"
                      className={
                        "bk-checkin" + (booking.checked_in ? " is-on" : "")
                      }
                      onClick={() => handleCheckIn(booking)}
                      disabled={!!checkinLoading[id]}
                    >
                      {booking.checked_in ? "✓ Checked In" : "Check In"}
                    </button>
                    <button
                      type="button"
                      className="bk-editbtn"
                      onClick={() => startEditing(booking)}
                    >
                      Edit Tour
                    </button>
                    <div className="bk-subrow">
                      <button
                        type="button"
                        className="bk-ticketbtn"
                        onClick={() => downloadTicket(booking)}
                      >
                        Ticket
                      </button>
                      <button
                        type="button"
                        className="bk-delbtn"
                        onClick={() => setDeletingBooking(booking)}
                        title="Delete booking"
                        aria-label="Delete booking"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ---- Edit Tour modal (design pattern, legacy PUT) ---- */}
      {editingBooking && (
        <div
          className="adm-modal-overlay"
          onClick={() => {
            if (!updateLoading) cancelEditing();
          }}
        >
          <div
            className="adm-modal bk-editmodal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="adm-modal-title bk-editmodal-title">
              Edit tour date &amp; time
            </div>
            <label className="adm-field">
              <span className="adm-label">Tour Date</span>
              <input
                type="date"
                className="adm-input"
                value={editForm.tour_date}
                onChange={(e) =>
                  setEditForm({ ...editForm, tour_date: e.target.value })
                }
              />
            </label>
            <label className="adm-field bk-field--last">
              <span className="adm-label">Tour Time</span>
              <select
                className="adm-select"
                value={editForm.tour_time}
                onChange={(e) =>
                  setEditForm({ ...editForm, tour_time: e.target.value })
                }
              >
                {editTimeIsCustom && (
                  <option value={editForm.tour_time}>
                    {formatTime(editForm.tour_time)}
                  </option>
                )}
                {TIME_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <div className="adm-modal-actions">
              <button
                type="button"
                className="adm-btn adm-btn--soft"
                onClick={cancelEditing}
                disabled={updateLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="adm-btn"
                onClick={handleUpdateTour}
                disabled={updateLoading}
              >
                {updateLoading ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- delete confirm (design copy, legacy DELETE) ---- */}
      <ConfirmModal
        open={!!deletingBooking}
        title="Cancel this booking?"
        body="The booking will be permanently removed. This cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Keep Booking"
        danger
        busy={deleteBusy}
        onConfirm={confirmDelete}
        onCancel={() => {
          if (!deleteBusy) setDeletingBooking(null);
        }}
      />
      </div>
    </AdminShell>
  );
};

export default RAdminBookings;
